import styled from 'styled-components';
import { useRouter } from 'next/router';
import { addToFavouritesEvent } from '@lib/gtm';

import { useTranslation } from '@hooks';
import { useAccount } from '@context/AccountContext';
import { FavoriteIcon, FilledFavoriteIcon } from '@components/icons';
import { Caption } from '@components/styles/Typography';

const ProductFavorite = ({ forDesktop, forListing, product }) => {
    const { currency } = useTranslation();
    const { id, favorite_count: favoriteCount } = product;
    const router = useRouter();
    const {
        addToFavoritesMutation,
        currentUser,
        deleteFavoriteMutation,
        isFavorite,
    } = useAccount();

    const handleClick = () => {
        if (!currentUser) {
            router.push({
                pathname: '/login',
                query: { cb: router.asPath, favoriteModelID: id },
            });
            return;
        }
        if (isFavorite(id)) {
            deleteFavoriteMutation.mutate({
                modelID: id,
                token: currentUser,
            });
        } else {
            addToFavoritesMutation.mutate({
                modelID: id,
                token: currentUser,
            });
            addToFavouritesEvent({ product, currency });
        }
    };

    return (
        <IconWrapper forDesktop={forDesktop} forListing={forListing}>
            <div
                aria-label="FavoriteButton"
                onClick={handleClick}
                onKeyPress={handleClick}
                role="button"
                tabIndex="0"
                data-testid="product-favorite-button"
            >
                {isFavorite(id) ? (
                    <FavoriteIconContainer
                        filled
                        data-testid="filled-favorite-button"
                    >
                        <Caption>{favoriteCount}</Caption>
                        <FilledFavoriteIcon />
                    </FavoriteIconContainer>
                ) : (
                    <FavoriteIconContainer>
                        <FavoriteIcon data-testid="add-to-favorite-button" />
                    </FavoriteIconContainer>
                )}
            </div>
        </IconWrapper>
    );
};

const IconWrapper = styled.div`
    display: flex;
    position: ${({ forListing }) => (forListing ? 'absolute' : 'sticky')};
    margin-left: auto;
    margin-right: ${({ forListing }) => (forListing ? 'unset' : '55px')};
    right: ${({ forListing }) => (forListing ? '43px' : '55px')};
    top: ${({ forListing }) => (forListing ? '15px' : 'calc(100vh - 50px)')};
    align-items: center;
    z-index: 3;
    width: 12px;
    height: 0;

    @media (max-width: 1024px) {
        position: absolute;
        top: ${({ forListing }) => (forListing ? 0 : 'unset')};
        bottom: ${({ forListing }) => !forListing && '28px'};
        right: ${({ forListing }) => (forListing ? '43px' : '40px')};
        align-items: center;
        margin-left: unset;
        margin-right: unset;
        height: unset;
    }
`;

const FavoriteIconContainer = styled.div`
    width: 40px;
    height: 40px;
    object-fit: contain;
    margin-top: 5px;
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-right: ${({ forDesktop }) => forDesktop && '5px'};
    cursor: pointer;

    span {
        animation: fadeIn 1s;
    }

    svg {
        animation: ${({ filled }) =>
            filled
                ? 'heart-bounce .5s cubic-bezier(.7,0,.3,1);'
                : 'fadeIn 1s;'};
        margin-left: 4px;
    }

    @keyframes heart-bounce {
        0% {
            transform: scale(0.7);
        }
        80%,
        100% {
            transform: scale(1);
        }
    }
    @keyframes fadeIn {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
`;
export default ProductFavorite;
